import { ChevronRight, CornerLeftUp } from 'lucide-react';
import { createContext, useContext, useId, useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nucleus-commerce/ui/components/collapsible';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { SchemaConstraints, SchemaNode } from '../generated/reference';

import { circularOf, expandable, formatValue, schemaTypeLabel } from './schema-viewer-type';

type ExpandMode = 'default' | 'all' | 'none';

interface TreeSettings {
  mode: ExpandMode;
  /** Levels of properties visible before anything is clicked. */
  openLevels: number;
  /** Prefix of every row id, unique per viewer, so cycle links find their row. */
  idPrefix: string;
}

const TreeContext = createContext<TreeSettings>({ mode: 'default', openLevels: 2, idPrefix: '' });

/** Row ids of the named schemas above a row, by `ref`: where a cut cycle links to. */
type Anchors = Readonly<Record<string, string>>;

export interface SchemaViewerProps {
  schema: SchemaNode;
  /** Levels of properties shown open at first; two by default (plan 8). */
  openLevels?: number;
}

/**
 * A dereferenced schema as a property tree: name, type, required, nullable, enum values,
 * constraints, default and example per row. Objects and arrays of objects expand; `oneOf`
 * switches between variants; a cut cycle links back to the row it repeats. Named schemas show
 * their name so `Cart` or `Money` read as they do in the SDK types.
 */
export function SchemaViewer({ schema, openLevels = 2 }: SchemaViewerProps) {
  const { t } = useTranslation('docs');
  const idPrefix = useId();
  const [mode, setMode] = useState<ExpandMode>('default');
  // Remounting the tree applies the new mode to every row's own open state.
  const [generation, setGeneration] = useState(0);
  const root = expandable(schema);
  const rootId = `${idPrefix}root`;
  const anchors: Anchors = {
    ...(schema.ref ? { [schema.ref]: rootId } : {}),
    ...(root?.ref ? { [root.ref]: rootId } : {}),
  };

  const toggleAll = () => {
    setMode(mode === 'all' ? 'none' : 'all');
    setGeneration((g) => g + 1);
  };

  return (
    <div className="rounded-lg border text-sm">
      <div
        id={rootId}
        tabIndex={-1}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b px-4 py-2 outline-none"
      >
        <code className="text-muted-foreground font-mono text-xs">{schemaTypeLabel(schema)}</code>
        {schema.nullable ? <FlagBadge>{t('schema.nullable')}</FlagBadge> : null}
        {root ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto h-7 text-xs"
            onClick={toggleAll}
          >
            {mode === 'all' ? t('schema.collapseAll') : t('schema.expandAll')}
          </Button>
        ) : null}
      </div>
      {schema.description ? (
        <p className="text-muted-foreground border-b px-4 py-2">
          <InlineCode text={schema.description} />
        </p>
      ) : null}
      <TreeContext value={{ mode, openLevels, idPrefix }}>
        {root ? (
          <SchemaChildren key={generation} node={root} depth={0} anchors={anchors} path="" />
        ) : (
          <div className="px-4 py-3">
            <NodeDetails node={{ ...schema, description: undefined }} anchors={anchors} />
          </div>
        )}
      </TreeContext>
    </div>
  );
}

/** The rows under an expandable node: its properties, or a variant switcher for `oneOf`. */
function SchemaChildren({
  node,
  depth,
  anchors,
  path,
}: {
  node: SchemaNode;
  depth: number;
  anchors: Anchors;
  path: string;
}) {
  const { t } = useTranslation('docs');
  if (node.kind === 'oneOf') {
    return <VariantSwitcher node={node} depth={depth} anchors={anchors} path={path} />;
  }
  const rows = [...(node.properties ?? [])];
  if (node.additionalProperties && rows.length > 0) {
    rows.push({ ...node.additionalProperties, name: t('schema.key'), required: false });
  }
  if (rows.length === 0) {
    return <p className="text-muted-foreground px-4 py-3">{t('schema.empty')}</p>;
  }
  return (
    <ul className="divide-y">
      {rows.map((row, index) => (
        <PropertyRow
          key={`${row.name ?? ''}-${index}`}
          node={row}
          depth={depth}
          anchors={anchors}
          path={`${path}.${row.name ?? index}`}
        />
      ))}
    </ul>
  );
}

function PropertyRow({
  node,
  depth,
  anchors,
  path,
}: {
  node: SchemaNode;
  depth: number;
  anchors: Anchors;
  path: string;
}) {
  const { t } = useTranslation('docs');
  const { mode, openLevels, idPrefix } = useContext(TreeContext);
  const [open, setOpen] = useState(
    mode === 'all' ? true : mode === 'none' ? false : depth + 1 < openLevels,
  );
  const id = `${idPrefix}${path}`;
  const child = expandable(node);
  const childAnchors: Anchors = {
    ...anchors,
    ...(node.ref ? { [node.ref]: id } : {}),
    ...(child?.ref ? { [child.ref]: id } : {}),
  };
  const name = node.name ?? '';

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen}>
      <li id={id} tabIndex={-1} data-depth={depth} className="px-4 py-3 outline-none">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {child ? (
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('schema.toggle', { name })}
                className="-ml-1.5 size-6"
              >
                <ChevronRight
                  aria-hidden
                  className={cn('size-4 transition-transform', open && 'rotate-90')}
                />
              </Button>
            </CollapsibleTrigger>
          ) : null}
          <code className="font-mono font-semibold">{name}</code>
          <code className="text-muted-foreground font-mono text-xs">{schemaTypeLabel(node)}</code>
          {node.nullable ? <FlagBadge>{t('schema.nullable')}</FlagBadge> : null}
          {node.deprecated ? <FlagBadge>{t('schema.deprecated')}</FlagBadge> : null}
          {node.required ? (
            <span className="text-destructive text-xs font-medium">{t('schema.required')}</span>
          ) : null}
        </div>
        <div className={cn('mt-1 flex flex-col gap-1.5', child && 'pl-6')}>
          <NodeDetails node={node} anchors={anchors} />
        </div>
        {child ? (
          <CollapsibleContent>
            <div className="mt-3 ml-2 rounded-md border">
              <SchemaChildren node={child} depth={depth + 1} anchors={childAnchors} path={path} />
            </div>
          </CollapsibleContent>
        ) : null}
      </li>
    </Collapsible>
  );
}

/** Description, enum values, constraints, default, example, and the link of a cut cycle. */
function NodeDetails({ node, anchors }: { node: SchemaNode; anchors: Anchors }) {
  const { t } = useTranslation('docs');
  const circular = circularOf(node);
  const target = circular?.ref ? anchors[circular.ref] : undefined;
  const constraints = constraintEntries(node.constraints ?? node.items?.constraints).map(
    ([key, value]) => t(`schema.constraints.${key}`, { value: String(value) }),
  );
  const enumValues = node.enum ?? node.items?.enum;

  const jump = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!target) return;
    event.preventDefault();
    const row = document.getElementById(target);
    row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    row?.focus({ preventScroll: true });
  };

  return (
    <>
      {node.description ? (
        <p className="text-muted-foreground">
          <InlineCode text={node.description} />
        </p>
      ) : null}
      {enumValues?.length ? (
        <div className="flex flex-wrap gap-1">
          {enumValues.map((value) => (
            <Badge key={String(value)} variant="outline" className="font-mono">
              {formatValue(value)}
            </Badge>
          ))}
        </div>
      ) : null}
      {constraints.length || node.default !== undefined || node.example !== undefined ? (
        <p className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {constraints.map((label) => (
            <span key={label}>{label}</span>
          ))}
          {node.default !== undefined ? (
            <span>
              {t('schema.default')}:{' '}
              <code className="text-foreground font-mono">{formatValue(node.default)}</code>
            </span>
          ) : null}
          {node.example !== undefined ? (
            <span className="min-w-0 break-all">
              {t('schema.example')}:{' '}
              <code className="text-foreground font-mono">{formatValue(node.example)}</code>
            </span>
          ) : null}
        </p>
      ) : null}
      {circular?.ref ? (
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <CornerLeftUp aria-hidden className="size-3.5" />
          {target ? (
            <a
              href={`#${target}`}
              onClick={jump}
              title={t('schema.circularHint', { name: circular.ref })}
              className="text-foreground underline underline-offset-4"
            >
              {t('schema.circular', { name: circular.ref })}
            </a>
          ) : (
            <span>{t('schema.circularHint', { name: circular.ref })}</span>
          )}
        </p>
      ) : null}
    </>
  );
}

function VariantSwitcher({
  node,
  depth,
  anchors,
  path,
}: {
  node: SchemaNode;
  depth: number;
  anchors: Anchors;
  path: string;
}) {
  const { t } = useTranslation('docs');
  const [index, setIndex] = useState(0);
  const variants = node.variants ?? [];
  const selected = variants[index];
  const inner = selected ? expandable(selected) : null;

  return (
    <div className="flex flex-col">
      <div
        role="group"
        aria-label={t('schema.variants')}
        className="flex flex-wrap items-center gap-1 border-b px-4 py-2"
      >
        <span className="text-muted-foreground mr-1 text-xs">{t('schema.variants')}</span>
        {variants.map((variant, i) => (
          <Button
            key={i}
            type="button"
            size="sm"
            variant={i === index ? 'secondary' : 'ghost'}
            aria-pressed={i === index}
            className="h-7 font-mono text-xs"
            onClick={() => setIndex(i)}
          >
            {variant.ref ?? variant.name ?? t('schema.variant', { index: i + 1 })}
          </Button>
        ))}
      </div>
      {selected && inner ? (
        <SchemaChildren
          key={index}
          node={inner}
          depth={depth}
          anchors={anchors}
          path={`${path}~${index}`}
        />
      ) : selected ? (
        <div className="flex flex-col gap-1.5 px-4 py-3">
          <code className="text-muted-foreground font-mono text-xs">
            {schemaTypeLabel(selected)}
          </code>
          <NodeDetails node={selected} anchors={anchors} />
        </div>
      ) : null}
    </div>
  );
}

function FlagBadge({ children }: { children: ReactNode }) {
  return (
    <Badge variant="secondary" className="h-5 px-1.5 font-normal">
      {children}
    </Badge>
  );
}

/** Spec descriptions use backticks for code; everything else stays plain text. */
function InlineCode({ text }: { text: string }) {
  return text.split(/(`[^`]+`)/).map((part, index) =>
    part.startsWith('`') && part.endsWith('`') && part.length > 1 ? (
      <code key={index} className="bg-muted rounded px-1 py-0.5 font-mono text-[0.85em]">
        {part.slice(1, -1)}
      </code>
    ) : (
      part
    ),
  );
}

function constraintEntries(
  constraints: SchemaConstraints | undefined,
): Array<[keyof SchemaConstraints, number | string]> {
  if (!constraints) return [];
  return (Object.keys(constraints) as Array<keyof SchemaConstraints>).flatMap((key) => {
    const value = constraints[key];
    return value === undefined ? [] : [[key, value] as [keyof SchemaConstraints, number | string]];
  });
}
